import React, { memo, useCallback, useMemo, useState } from 'react';
import { TabHeaderItem, TabsHeader, showYesNo } from '@modules/common';
import { Badge } from 'reactstrap';
import { ProposalsPageBloc } from './ProposalsPageBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { useBloc } from '@core/utils/bloc';
import moment from 'moment';
import { TopLoadingBar } from '@core/components/LoadingBar';
// import { openAddProposalModal } from '@modules/proposals/AddProposalModal';
import { CBody } from '@modules/common/CBody';
import { GCTable, GCColumn } from '@core/components/GCTable';
import { CompactProposalTableResponse, ProposalStatus } from '@business/entities/proposal';
import { R } from '@core/utils/r';
import { openViewProposal } from '@modules/proposals/ViewProposalModal';
import { ProposalService } from '@business/services/proposal_service';
import { MoreMenu } from '@modules/common/MoreMenu';
import { toMoment } from '@core/utils/time';
import { OrgId } from '@business/entities/organization';
import { Formatter } from '@core/utils/formatter';
import { switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { useHistory } from '@core/router';
import { Session } from '@modules/auth/session';
import { UserRole } from '@business/entities/account';

interface ProposalsPageProps {
  orgId: OrgId;
  orgName?: string;
  showAction?: boolean;
  onClickPreviewProposal?: boolean;
  statusFilter?: boolean;
}

export const ProposalsPage = memo((props: ProposalsPageProps) => {
  const { orgId, orgName, showAction = true, onClickPreviewProposal = false, statusFilter = false } = props;
  const bloc = useBloc(ProposalsPageBloc, orgId);
  const loading = useLoadingState(bloc.loading);
  const user = useObservable(Session.user$);
  const blocProposals = useObservable(bloc.proposals$, []) as CompactProposalTableResponse[];

  const proposals = useMemo(() => {
    return blocProposals.filter(p => {
      const isOrgMatch = R.toNumber(p.orgId) === R.toNumber(orgId);
      const isStatusMatch = statusFilter ? !['draft'].includes(p.status) : true;
      return isOrgMatch && isStatusMatch;
    });
  }, [blocProposals, orgId, statusFilter]);
  const [status, setStatus] = useState<ProposalStatus | 'archived' | null>(null);
  const history = useHistory();

  const isClientPortal = useMemo(() => user.role === UserRole.ClientPortal, [user.role]);

  const tabs: TabHeaderItem[] = useMemo(() => {
    return [
      { title: 'All' },
      ...(isClientPortal ? [] : [{ title: 'Drafts' }]),
      { title: isClientPortal ? 'Received' : 'Sent' },
      { title: 'Viewed' },
      { title: 'Signed' },
      ...(isClientPortal ? [] : [{ title: 'Archived' }]),
    ];
  }, [isClientPortal]);

  const onProposalClick = useCallback(
    (row: CompactProposalTableResponse) => {
      if (row.status === ProposalStatus.draft) {
        // if (Config.IS_STAGE) {
        history.push(`/add-edit-proposal/${row.estimateId}/${row.id}?lastUrl=/organizations/${row.orgId}/proposals`);
        // } else {
        //   openAddProposalModal({ proposalId: row.id, orgId: props.orgId, lastUrl: `/organizations/${row.orgId}/proposals` });
        // }
      } else if (onClickPreviewProposal) {
        const proposalName = `${user.firstName} ${user.lastName}`;
        const signerName = Object.keys(row?.signerUrl);
        if (signerName.length > 0) {
          if (proposalName === signerName[0]) {
            window.open(row.signerUrl[signerName[0]], '_blank');
          } else {
            window.open(row.previewUrl, '_blank');
          }
        } else {
          window.open(row.previewUrl, '_blank');
        }
      } else {
        openViewProposal({ proposalId: row.id, lastUrl: `/organizations/${row.orgId}/proposals` });
      }
    },
    [props.orgId, history],
  );

  const columns: GCColumn<CompactProposalTableResponse>[] = useMemo(
    () => [
      {
        name: 'Title',
        sortField: 'title',
        minWidth: '300px',
        // sortable: true,
        cell: row => {
          return (
            <div className="d-flex flex-row mt-2 mb-2">
              <div className="d-flex flex-row mr-2 mb-2 align-items-center" onClick={() => onProposalClick(row)}>
                <div style={{ position: 'relative' }}>
                  <img
                    style={{
                      width: 80,
                      height: 105,
                      objectFit: 'cover',
                      objectPosition: 'top',
                      borderRadius: 10,
                    }}
                    src={row.image}
                  />
                  <Badge
                    className="d-flex flex-row p-1 align-items-center text-center"
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      right: 0,
                      fontSize: 12,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      borderBottomLeftRadius: 10,
                      borderBottomRightRadius: 10,
                      justifyContent: 'center',
                    }}
                    color={row.isExpired && !row.deletedAt ? 'danger' : ProposalService.getStatusColor(row.status, !!row.deletedAt)}
                  >
                    {row.deletedAt
                      ? 'Archived'
                      : row.isExpired
                      ? 'Expired'
                      : R.upperFirst(row.status == ProposalStatus.sent && isClientPortal ? ProposalStatus.received : row.status)}
                  </Badge>
                </div>
              </div>

              <div className="d-flex flex-column justify-content-center">
                <span className={`text-dark font-weight-medium font-size-16`} onClick={() => onProposalClick(row)}>
                  {row.title}
                </span>

                {row.signer ? (
                  <span className="text-muted font-size-12">
                    {isClientPortal ? R.upperFirst(ProposalStatus.received) : R.upperFirst(ProposalStatus.sent)}:{' '}
                    {toMoment(row.created).format('MMM, DD YYYY')} - Signed: {moment(row.signer.signed_at).format('MMM, DD YYYY hh:mm A')}
                  </span>
                ) : (
                  <span className="text-muted font-size-12">Sent: {toMoment(row.created).format('MMM, DD YYYY')}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        name: 'Amount',
        sortField: 'amount',
        width: '200px',
        cell: row => {
          return (
            <div onClick={() => onProposalClick(row)}>
              <div className="d-flex flex-row mb-1">
                <span className={`text-dark font-weight-medium font-size-14`}>{Formatter.formatCurrency(row.amount, 'USD')}</span>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Status',
        sortField: 'status',
        width: '100px',
        cell: row => (
          <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ alignItems: 'center', flexDirection: 'row', display: 'flex' }}>
              <div className="d-flex">
                <Badge className="d-flex flex-row mr-2 p-1 align-items-center" color={ProposalService.getStatusColor(row.status)}>
                  {R.upperFirst(row.status == ProposalStatus.sent && isClientPortal ? ProposalStatus.received : row.status)}
                </Badge>
                {showAction && (
                  <MoreMenu
                    className="float-right"
                    vertical
                    items={[
                      // {
                      //   icon: 'fa fa-envelope',
                      //   title: 'Resend Proposal',
                      //   onClick: () => openResendProposal({ proposalId: row.id, orgId: row.orgId }),
                      // },
                      // {
                      //   icon: 'fa fa-archive',
                      //   className: 'text-danger',
                      //   title: 'Archive',
                      //   onClick: () => {
                      //     bloc.deleteProposal(row.id).subscribe();
                      //   },
                      // },
                      {
                        icon: 'fa fa-archive',
                        className: 'text-danger',
                        title: !row.deletedAt ? 'Archive' : 'Unarchive',
                        //   visible: !row.deletedAt,
                        onClick: () => {
                          if (row.deletedAt) {
                            of(null)
                              .pipe(
                                switchMap(() => showYesNo('Un-Archive Proposal', `Do you really want to unarchive this proposal?`)),
                                tap(res => {
                                  if (res)
                                    bloc
                                      .unarchiveProposal(row.id)
                                      .pipe(tap(() => bloc.onReload().subscribe()))
                                      .subscribe();
                                }),
                              )
                              .subscribe();
                          } else {
                            bloc
                              .deleteProposal(row.id)
                              .pipe(tap(() => bloc.onReload().subscribe()))
                              .subscribe();
                          }
                        },
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          </div>
        ),
      },
    ],
    [bloc, onProposalClick, isClientPortal],
  );

  const onTabIndexChange = useCallback(
    (index: number) => {
      if (!index) return setStatus(null);
      if (isClientPortal) {
        if (index === 1) return setStatus(ProposalStatus.sent);
        if (index === 2) return setStatus(ProposalStatus.viewed);
        if (index === 3) return setStatus(ProposalStatus.accepted);
      } else {
        if (index === 1) return setStatus(ProposalStatus.draft);
        if (index === 2) return setStatus(ProposalStatus.sent);
        if (index === 3) return setStatus(ProposalStatus.viewed);
        if (index === 4) return setStatus(ProposalStatus.accepted);
        if (index === 5) return setStatus(ProposalStatus.archived);
      }
      // if (index === 5) return setStatus(ProposalStatus.void);
      // if (index === 6) return setStatus(ProposalStatus.cancel);
    },
    [isClientPortal],
  );

  const items = useMemo(() => {
    const filtered =
      status && status == ProposalStatus.archived
        ? proposals.filter(p => p.deletedAt)
        : proposals.filter(p => !status || (p.status === status && !p.deletedAt));
    const resp = R.sortBy(filtered, u => u.created);
    return resp.reverse();
  }, [proposals, status]);

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div className="main_tabs">
      {/* <div className="d-flex justify-content-between align-items-center">
        <CardTitle className="mb-0"></CardTitle>
        <div className="d-flex align-items-center">
          <Button
            color="primary"
            className="btn btn-primary btn-sm"
            onClick={() => {
              // if (Config.IS_STAGE) {
                history.push(`/add-edit-proposal/1?orgId=${orgId}&orgName=${encodeURIComponent(orgName)}&lastUrl=/organizations/${orgId}/proposals`);
              // } else {
              //   openAddProposalModal({ orgId, fullOrgResponse: true, lastUrl: `/organizations/${orgId}/proposals` });
              // }
            }}
          >
            <i className="fa fa-plus mr-2"></i>
            Send Proposal
          </Button>
        </div>
      </div> */}
      <TabsHeader pills tabs={tabs} onChange={onTabIndexChange} />
      <CBody>
        <GCTable
          noTableHead
          customStyles={{
            rows: {
              style: { transition: 'all 0.25s ease-in-out' },
              highlightOnHoverStyle: { backgroundColor: '#556ee620', cursor: 'pointer', borderRadius: 5 },
            },
          }}
          onRowClicked={row => R.noop()}
          highlightOnHover
          pointerOnHover
          noSearch
          striped={false}
          bordered={false}
          noHeader
          sortServer
          columns={columns}
          data={items}
          paginationRowsPerPageOptions={[25, 50, 100]}
          noQueryOnSearch
        />
      </CBody>
    </div>
  );
});
